import React, { useState, useEffect, useRef } from 'react';
import { User, GameStats } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import StatBox from '../ui/StatBox';
import OnScreenKeyboard from '../ui/OnScreenKeyboard';
import Confetti from '../ui/Confetti';
import { StarRating } from '../games/GameShell';
import { api } from '../../utils/api';

interface TypingGameProps {
  user: User;
}

const TypingGame: React.FC<TypingGameProps> = ({ user }) => {
  const [words, setWords] = useState<string[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [gameStats, setGameStats] = useState<GameStats>({ wpm: 0, acc: 100, correct: 0, incorrect: 0 });
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameStarted, setGameStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // FIX: The return type of `setInterval` in browser environments is `number`, not `NodeJS.Timeout`.
  const timerInterval = useRef<number | null>(null);

  const fetchWords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<string[]>('/api/v1/game/words');
      setWords(data);
    } catch (e) {
      if(e instanceof Error) setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWords();
  }, []);

  useEffect(() => {
    if (gameStarted && timeLeft > 0) {
      timerInterval.current = window.setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    } else if (timeLeft === 0 && timerInterval.current) {
      clearInterval(timerInterval.current);
      setGameStarted(false);
      calculateStats();
    }
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, timeLeft]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [gameStarted]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        if (timeLeft === 0) return;

        setPressedKey(e.key);
        setTimeout(() => setPressedKey(null), 100);

        if (!gameStarted) {
            setGameStarted(true);
        }
        
        const currentWord = words[currentWordIndex] || "";

        if (e.key === ' ') {
            e.preventDefault();
            if (typedText.length === 0) return; // Don't submit on empty space
            if (typedText === currentWord) {
                setGameStats(prev => ({ ...prev, correct: prev.correct + 1 }));
            } else {
                setGameStats(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
            }
            setCurrentWordIndex(prev => prev + 1);
            setTypedText("");
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            setTypedText(prev => prev.slice(0, -1));
        } else if (e.key.length === 1 && typedText.length < currentWord.length + 5) { // Allow some over-typing
            e.preventDefault();
            setTypedText(prev => prev + e.key);
        }
    };

    const inputEl = inputRef.current;
    inputEl?.addEventListener('keydown', handleKeyDown);
    return () => inputEl?.removeEventListener('keydown', handleKeyDown);
}, [typedText, timeLeft, gameStarted, words, currentWordIndex]);

  const calculateStats = () => {
    const totalCharsTyped = words.slice(0, currentWordIndex).reduce((acc, word) => acc + word.length + 1, 0);
    const wpm = Math.round(totalCharsTyped / 5);
    const totalWordsTyped = gameStats.correct + gameStats.incorrect;
    const acc = totalWordsTyped > 0 ? Math.round((gameStats.correct / totalWordsTyped) * 100) : 100;
    setGameStats(prev => ({ ...prev, wpm, acc }));

    if (user && user.user_id !== 'guest' && wpm > 0) {
        api.post('/api/v1/student/game-progress', { game_name: 'timed-race', high_score: wpm, increment_attempts: true }).catch(() => {});
    }
  };

  const resetGame = () => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    setCurrentWordIndex(0);
    setTypedText("");
    setGameStats({ wpm: 0, acc: 100, correct: 0, incorrect: 0 });
    setTimeLeft(60);
    setGameStarted(false);
    fetchWords();
  };

  const renderWord = (word: string, index: number) => {
    if (index === currentWordIndex) {
      return word.split("").map((char, charIndex) => {
        let charClass = "text-gray-500";
        if (charIndex < typedText.length) charClass = typedText[charIndex] === char ? "text-green-600" : "text-red-500 bg-red-100";
        else if (charIndex === typedText.length) charClass = "text-blue-600 border-b-2 border-blue-600";
        return <span key={charIndex} className={charClass}>{char}</span>;
      });
    }
    return <span className={index < currentWordIndex ? "text-gray-400" : "text-gray-800"}>{word}</span>;
  };
  
  if (loading) return <LoadingSpinner message="Loading game..." />;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (
    <div className="w-full" onClick={() => inputRef.current?.focus()}>
      <p className="text-center text-sm font-semibold text-gray-600 mb-4">⏱️ The 60-second clock starts on your first keystroke!</p>
      <div className="flex flex-wrap justify-around mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Time Left" value={timeLeft} />
        <StatBox label="WPM" value={gameStats.wpm} />
        <StatBox label="Correct" value={gameStats.correct} />
        <StatBox label="Incorrect" value={gameStats.incorrect} />
      </div>

      {timeLeft > 0 ? (
        <div className="p-4 md:p-6 mb-6 bg-white rounded-2xl border-4 border-sky-200 shadow-md overflow-x-auto whitespace-nowrap font-mono text-2xl md:text-3xl font-bold" style={{ minHeight: '150px' }}>
          {words.slice(currentWordIndex, currentWordIndex + 10).map((word, index) => <span key={index} className="mr-2">{renderWord(word, currentWordIndex + index)}</span>)}
        </div>
      ) : (
        <div className="relative p-4 md:p-6 mb-6 bg-white/80 rounded-3xl border-4 border-white shadow-md text-center" style={{ minHeight: '150px' }}>
            {gameStats.wpm > 0 && <Confetti />}
            <h3 className="fun-pop text-4xl font-black bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">Time's Up!</h3>
            <StarRating stars={gameStats.wpm >= 30 ? 3 : gameStats.wpm >= 15 ? 2 : gameStats.wpm > 0 ? 1 : 0} />
            <p className="text-xl text-gray-700 mt-3">You typed <span className="font-black text-blue-600">{gameStats.wpm} WPM</span> with <span className="font-black text-emerald-600">{gameStats.acc}%</span> accuracy.</p>
            <button onClick={resetGame} className="fun-shine mt-5 px-8 py-3 text-white rounded-full font-black text-xl shadow-lg ring-4 ring-white bg-gradient-to-r from-sky-500 to-blue-600 transition-transform hover:scale-105 active:scale-95">🔁 Play Again</button>
        </div>
      )}

      <input ref={inputRef} type="text" className="opacity-0 absolute w-0 h-0" aria-hidden="true" autoFocus disabled={timeLeft === 0} />
      <OnScreenKeyboard nextChar={timeLeft === 0 || !words[currentWordIndex] ? null : words[currentWordIndex][typedText.length]} isTutorial={false} lessonId={null} isError={false} lessonKeys={null} pressedKey={pressedKey} />
    </div>
  );
};

export default TypingGame;